// This file is auto-generated by @hey-api/openapi-ts

import { type DefaultError, queryOptions, type UseMutationOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { client } from '../client.gen';
import { addCaseToGroup, addCorrection, createCase, createRelease, createReview, createSuite, createTestGroup, deleteCase, deleteCaseRun, getCase, getCaseRun, getDocumentContent, getImportStatus, getIntake, getRegistry, getRelease, getReviewQueue, getSuite, getSuiteReport, getTestGroup, health, importDocuments, listAuditFields, listCaseRuns, listCases, listCorrections, listDocuments, listTestGroups, type Options, removeCaseFromGroup, retryAwaitingCases, reviewAuditField, reviseReview, startCaseRun, startSuite, updateLabel } from '../sdk.gen';
import type { AddCaseToGroupData, AddCaseToGroupError, AddCaseToGroupResponse, AddCorrectionData, AddCorrectionError, AddCorrectionResponse, CreateCaseData, CreateCaseError, CreateCaseResponse, CreateReleaseData, CreateReleaseError, CreateReleaseResponse, CreateReviewData, CreateReviewError, CreateReviewResponse, CreateSuiteData, CreateSuiteError, CreateSuiteResponse, CreateTestGroupData, CreateTestGroupError, CreateTestGroupResponse, DeleteCaseData, DeleteCaseError, DeleteCaseResponse, DeleteCaseRunData, DeleteCaseRunError, DeleteCaseRunResponse, GetCaseData, GetCaseError, GetCaseResponse, GetCaseRunData, GetCaseRunError, GetCaseRunResponse, GetDocumentContentData, GetDocumentContentError, GetDocumentContentResponse, GetImportStatusData, GetImportStatusError, GetImportStatusResponse, GetIntakeData, GetIntakeError, GetIntakeResponse, GetRegistryData, GetRegistryError, GetReleaseData, GetReleaseError, GetReleaseResponse, GetReviewQueueData, GetReviewQueueError, GetReviewQueueResponse, GetSuiteData, GetSuiteError, GetSuiteReportData, GetSuiteReportError, GetSuiteReportResponse, GetSuiteResponse, GetTestGroupData, GetTestGroupError, GetTestGroupResponse, HealthData, ImportDocumentsData, ImportDocumentsError, ImportDocumentsResponse, ListAuditFieldsData, ListAuditFieldsError, ListAuditFieldsResponse, ListCaseRunsData, ListCaseRunsError, ListCaseRunsResponse, ListCasesData, ListCasesError, ListCasesResponse, ListCorrectionsData, ListCorrectionsError, ListCorrectionsResponse, ListDocumentsData, ListDocumentsError, ListDocumentsResponse, ListTestGroupsData, ListTestGroupsError, ListTestGroupsResponse, RemoveCaseFromGroupData, RemoveCaseFromGroupError, RemoveCaseFromGroupResponse, RetryAwaitingCasesData, RetryAwaitingCasesError, RetryAwaitingCasesResponse, ReviewAuditFieldData, ReviewAuditFieldError, ReviewAuditFieldResponse, ReviseReviewData, ReviseReviewError, ReviseReviewResponse, StartCaseRunData, StartCaseRunError, StartCaseRunResponse, StartSuiteData, StartSuiteError, StartSuiteResponse, UpdateLabelData, UpdateLabelError, UpdateLabelResponse } from '../types.gen';

export type QueryKey<TOptions extends Options> = [
    Pick<TOptions, 'baseURL' | 'body' | 'headers' | 'path' | 'query'> & {
        _id: string;
        _infinite?: boolean;
        tags?: ReadonlyArray<string>;
    }
];

const createQueryKey = <TOptions extends Options>(id: string, options?: TOptions, infinite?: boolean, tags?: ReadonlyArray<string>): [
    QueryKey<TOptions>[0]
] => {
    const params: QueryKey<TOptions>[0] = { _id: id, baseURL: options?.baseURL || (options?.client ?? client).getConfig().baseURL } as QueryKey<TOptions>[0];
    if (infinite) {
        params._infinite = infinite;
    }
    if (tags) {
        params.tags = tags;
    }
    if (options?.body) {
        params.body = options.body;
    }
    if (options?.headers) {
        params.headers = options.headers;
    }
    if (options?.path) {
        params.path = options.path;
    }
    if (options?.query) {
        params.query = options.query;
    }
    return [params];
};

export const healthQueryKey = (options?: Options<HealthData>) => createQueryKey('health', options, false, ['status']);

/**
 * Health
 *
 * Liveness and readiness probe.
 */
export const healthOptions = (options?: Options<HealthData>) => queryOptions<unknown, AxiosError<DefaultError>, unknown, ReturnType<typeof healthQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await health({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: healthQueryKey(options)
});

export const getRegistryQueryKey = (options?: Options<GetRegistryData>) => createQueryKey('getRegistry', options, false, ['golden', 'registry']);

/**
 * Get Registry
 *
 * The scored field patterns and their comparators.
 */
export const getRegistryOptions = (options?: Options<GetRegistryData>) => queryOptions<unknown, AxiosError<GetRegistryError>, unknown, ReturnType<typeof getRegistryQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await getRegistry({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: getRegistryQueryKey(options)
});

export const listCasesQueryKey = (options?: Options<ListCasesData>) => createQueryKey('listCases', options, false, ['golden', 'cases']);

/**
 * List Cases
 */
export const listCasesOptions = (options?: Options<ListCasesData>) => queryOptions<ListCasesResponse, AxiosError<ListCasesError>, ListCasesResponse, ReturnType<typeof listCasesQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await listCases({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: listCasesQueryKey(options)
});

/**
 * Create Case
 *
 * Create a case from an RFP. Its baseline is the RFP's first carrier
 * submission, which bp-api and carrier-rfpq look up. An RFP with one makes a
 * draft case holding it; any other RFP makes a case awaiting a refresh. 400 when
 * source_rfp_id is not an RFP ID; 503, with nothing written, when the lookup
 * cannot be made. An RFP has at most one case that is not deleted: 409, with
 * nothing written and before any lookup, when it has one, and appError.message
 * names that case. An RFP whose cases are all deleted makes a new one.
 */
export const createCaseMutation = (options?: Partial<Options<CreateCaseData>>): UseMutationOptions<CreateCaseResponse, AxiosError<CreateCaseError>, Options<CreateCaseData>> => {
    const mutationOptions: UseMutationOptions<CreateCaseResponse, AxiosError<CreateCaseError>, Options<CreateCaseData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await createCase({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Delete Case
 *
 * Soft-delete a case: hidden, and out of every test group. The releases
 * and suite reports that already hold it keep it.
 */
export const deleteCaseMutation = (options?: Partial<Options<DeleteCaseData>>): UseMutationOptions<DeleteCaseResponse, AxiosError<DeleteCaseError>, Options<DeleteCaseData>> => {
    const mutationOptions: UseMutationOptions<DeleteCaseResponse, AxiosError<DeleteCaseError>, Options<DeleteCaseData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await deleteCase({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const getCaseQueryKey = (options: Options<GetCaseData>) => createQueryKey('getCase', options, false, ['golden', 'cases']);

/**
 * Get Case
 */
export const getCaseOptions = (options: Options<GetCaseData>) => queryOptions<GetCaseResponse, AxiosError<GetCaseError>, GetCaseResponse, ReturnType<typeof getCaseQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await getCase({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: getCaseQueryKey(options)
});

export const listCaseRunsQueryKey = (options: Options<ListCaseRunsData>) => createQueryKey('listCaseRuns', options, false, ['golden', 'runs']);

/**
 * List Case Runs
 *
 * The case's runs, newest first, each with the replay RFP RUMI filled.
 */
export const listCaseRunsOptions = (options: Options<ListCaseRunsData>) => queryOptions<ListCaseRunsResponse, AxiosError<ListCaseRunsError>, ListCaseRunsResponse, ReturnType<typeof listCaseRunsQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await listCaseRuns({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: listCaseRunsQueryKey(options)
});

/**
 * Start Case Run
 *
 * Run the deployed RUMI on a case, to qualify it as golden. The run
 * records the version that ran when it ends, and its review opens when it finishes.
 * A case has one case run in flight: a queued run is started again rather than
 * queued twice. 400 for an unknown replay kind; 409 for a case with no baseline,
 * no broker documents where documents are imported, or a run still running;
 * 503 when the run may not have started (try again to start the same run) or
 * this deployment runs no RUMI.
 */
export const startCaseRunMutation = (options?: Partial<Options<StartCaseRunData>>): UseMutationOptions<StartCaseRunResponse, AxiosError<StartCaseRunError>, Options<StartCaseRunData>> => {
    const mutationOptions: UseMutationOptions<StartCaseRunResponse, AxiosError<StartCaseRunError>, Options<StartCaseRunData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await startCaseRun({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Delete Case Run
 *
 * Soft-delete a finished or failed case run. Suite runs are kept.
 * 409 for a run that is queued or running, a suite's run, or a run a suite's
 * report takes as a reference run. When the case in review is left without
 * its audit sample, the review starts over from the earliest remaining scored
 * case run, or the case waits in draft.
 */
export const deleteCaseRunMutation = (options?: Partial<Options<DeleteCaseRunData>>): UseMutationOptions<DeleteCaseRunResponse, AxiosError<DeleteCaseRunError>, Options<DeleteCaseRunData>> => {
    const mutationOptions: UseMutationOptions<DeleteCaseRunResponse, AxiosError<DeleteCaseRunError>, Options<DeleteCaseRunData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await deleteCaseRun({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const getCaseRunQueryKey = (options: Options<GetCaseRunData>) => createQueryKey('getCaseRun', options, false, ['golden', 'runs']);

/**
 * Get Case Run
 *
 * A run with its latest score's totals and every discrepancy in that
 * score, reviewed or still open.
 */
export const getCaseRunOptions = (options: Options<GetCaseRunData>) => queryOptions<GetCaseRunResponse, AxiosError<GetCaseRunError>, GetCaseRunResponse, ReturnType<typeof getCaseRunQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await getCaseRun({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: getCaseRunQueryKey(options)
});

/**
 * Update Label
 */
export const updateLabelMutation = (options?: Partial<Options<UpdateLabelData>>): UseMutationOptions<UpdateLabelResponse, AxiosError<UpdateLabelError>, Options<UpdateLabelData>> => {
    const mutationOptions: UseMutationOptions<UpdateLabelResponse, AxiosError<UpdateLabelError>, Options<UpdateLabelData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await updateLabel({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Retry Awaiting Cases
 *
 * Look up the baseline again for every case that waits for a refresh, and
 * return the cases that found it. 503, with nothing written, when a lookup cannot
 * be made or this deployment has no lookup.
 */
export const retryAwaitingCasesMutation = (options?: Partial<Options<RetryAwaitingCasesData>>): UseMutationOptions<RetryAwaitingCasesResponse, AxiosError<RetryAwaitingCasesError>, Options<RetryAwaitingCasesData>> => {
    const mutationOptions: UseMutationOptions<RetryAwaitingCasesResponse, AxiosError<RetryAwaitingCasesError>, Options<RetryAwaitingCasesData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await retryAwaitingCases({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const getIntakeQueryKey = (options: Options<GetIntakeData>) => createQueryKey('getIntake', options, false, ['golden', 'documents']);

/**
 * Get Intake
 *
 * What the case's latest run gave RUMI: its replay's v5 export from
 * before RUMI ran. The replay is an intake copy of the source RFP made when the
 * run started, so it holds the broker's intake as the RFP stood then and none of
 * the analyst's build. 404 until a run of the case has recorded its intake.
 */
export const getIntakeOptions = (options: Options<GetIntakeData>) => queryOptions<GetIntakeResponse, AxiosError<GetIntakeError>, GetIntakeResponse, ReturnType<typeof getIntakeQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await getIntake({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: getIntakeQueryKey(options)
});

export const getImportStatusQueryKey = (options: Options<GetImportStatusData>) => createQueryKey('getImportStatus', options, false, ['golden', 'documents']);

/**
 * Get Import Status
 *
 * Whether the case's document import is running. A deployment that
 * imports no documents has none running. 503 when the job's state cannot be
 * read.
 */
export const getImportStatusOptions = (options: Options<GetImportStatusData>) => queryOptions<GetImportStatusResponse, AxiosError<GetImportStatusError>, GetImportStatusResponse, ReturnType<typeof getImportStatusQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await getImportStatus({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: getImportStatusQueryKey(options)
});

export const listDocumentsQueryKey = (options: Options<ListDocumentsData>) => createQueryKey('listDocuments', options, false, ['golden', 'documents']);

/**
 * List Documents
 */
export const listDocumentsOptions = (options: Options<ListDocumentsData>) => queryOptions<ListDocumentsResponse, AxiosError<ListDocumentsError>, ListDocumentsResponse, ReturnType<typeof listDocumentsQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await listDocuments({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: listDocumentsQueryKey(options)
});

export const getDocumentContentQueryKey = (options: Options<GetDocumentContentData>) => createQueryKey('getDocumentContent', options, false, ['golden', 'documents']);

/**
 * Get Document Content
 *
 * The tool's copy of a production broker document. Every view is logged.
 * 404 for an excluded document, another case's, or a copy that is missing; 503
 * when this deployment keeps no copies.
 */
export const getDocumentContentOptions = (options: Options<GetDocumentContentData>) => queryOptions<GetDocumentContentResponse, AxiosError<GetDocumentContentError>, GetDocumentContentResponse, ReturnType<typeof getDocumentContentQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await getDocumentContent({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: getDocumentContentQueryKey(options)
});

export const listCorrectionsQueryKey = (options: Options<ListCorrectionsData>) => createQueryKey('listCorrections', options, false, ['golden', 'corrections']);

/**
 * List Corrections
 */
export const listCorrectionsOptions = (options: Options<ListCorrectionsData>) => queryOptions<ListCorrectionsResponse, AxiosError<ListCorrectionsError>, ListCorrectionsResponse, ReturnType<typeof listCorrectionsQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await listCorrections({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: listCorrectionsQueryKey(options)
});

/**
 * Add Correction
 *
 * A correction on any field, without a discrepancy: for a field where RUMI
 * and the analyst made the same mistake.
 */
export const addCorrectionMutation = (options?: Partial<Options<AddCorrectionData>>): UseMutationOptions<AddCorrectionResponse, AxiosError<AddCorrectionError>, Options<AddCorrectionData>> => {
    const mutationOptions: UseMutationOptions<AddCorrectionResponse, AxiosError<AddCorrectionError>, Options<AddCorrectionData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await addCorrection({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const getReviewQueueQueryKey = (options?: Options<GetReviewQueueData>) => createQueryKey('getReviewQueue', options, false, ['golden', 'reviews']);

/**
 * Get Review Queue
 *
 * Open discrepancies, grouped by field key across cases.
 */
export const getReviewQueueOptions = (options?: Options<GetReviewQueueData>) => queryOptions<GetReviewQueueResponse, AxiosError<GetReviewQueueError>, GetReviewQueueResponse, ReturnType<typeof getReviewQueueQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await getReviewQueue({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: getReviewQueueQueryKey(options)
});

/**
 * Create Review
 *
 * One verdict on one discrepancy.
 */
export const createReviewMutation = (options?: Partial<Options<CreateReviewData>>): UseMutationOptions<CreateReviewResponse, AxiosError<CreateReviewError>, Options<CreateReviewData>> => {
    const mutationOptions: UseMutationOptions<CreateReviewResponse, AxiosError<CreateReviewError>, Options<CreateReviewData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await createReview({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Revise Review
 *
 * Set a discrepancy's verdict, or change the one it has, whatever the
 * case's state (review.revise_discrepancy).
 */
export const reviseReviewMutation = (options?: Partial<Options<ReviseReviewData>>): UseMutationOptions<ReviseReviewResponse, AxiosError<ReviseReviewError>, Options<ReviseReviewData>> => {
    const mutationOptions: UseMutationOptions<ReviseReviewResponse, AxiosError<ReviseReviewError>, Options<ReviseReviewData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await reviseReview({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const listAuditFieldsQueryKey = (options: Options<ListAuditFieldsData>) => createQueryKey('listAuditFields', options, false, ['golden', 'reviews']);

/**
 * List Audit Fields
 *
 * The audit sample, with the value RUMI and the analyst agreed on, from
 * the sampled run's latest score, so the reviewer can check it.
 */
export const listAuditFieldsOptions = (options: Options<ListAuditFieldsData>) => queryOptions<ListAuditFieldsResponse, AxiosError<ListAuditFieldsError>, ListAuditFieldsResponse, ReturnType<typeof listAuditFieldsQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await listAuditFields({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: listAuditFieldsQueryKey(options)
});

/**
 * Review Audit Field
 */
export const reviewAuditFieldMutation = (options?: Partial<Options<ReviewAuditFieldData>>): UseMutationOptions<ReviewAuditFieldResponse, AxiosError<ReviewAuditFieldError>, Options<ReviewAuditFieldData>> => {
    const mutationOptions: UseMutationOptions<ReviewAuditFieldResponse, AxiosError<ReviewAuditFieldError>, Options<ReviewAuditFieldData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await reviewAuditField({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const listTestGroupsQueryKey = (options?: Options<ListTestGroupsData>) => createQueryKey('listTestGroups', options, false, ['golden', 'test-groups']);

/**
 * List Test Groups
 */
export const listTestGroupsOptions = (options?: Options<ListTestGroupsData>) => queryOptions<ListTestGroupsResponse, AxiosError<ListTestGroupsError>, ListTestGroupsResponse, ReturnType<typeof listTestGroupsQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await listTestGroups({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: listTestGroupsQueryKey(options)
});

/**
 * Create Test Group
 */
export const createTestGroupMutation = (options?: Partial<Options<CreateTestGroupData>>): UseMutationOptions<CreateTestGroupResponse, AxiosError<CreateTestGroupError>, Options<CreateTestGroupData>> => {
    const mutationOptions: UseMutationOptions<CreateTestGroupResponse, AxiosError<CreateTestGroupError>, Options<CreateTestGroupData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await createTestGroup({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const getTestGroupQueryKey = (options: Options<GetTestGroupData>) => createQueryKey('getTestGroup', options, false, ['golden', 'test-groups']);

/**
 * Get Test Group
 */
export const getTestGroupOptions = (options: Options<GetTestGroupData>) => queryOptions<GetTestGroupResponse, AxiosError<GetTestGroupError>, GetTestGroupResponse, ReturnType<typeof getTestGroupQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await getTestGroup({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: getTestGroupQueryKey(options)
});

/**
 * Remove Case From Group
 */
export const removeCaseFromGroupMutation = (options?: Partial<Options<RemoveCaseFromGroupData>>): UseMutationOptions<RemoveCaseFromGroupResponse, AxiosError<RemoveCaseFromGroupError>, Options<RemoveCaseFromGroupData>> => {
    const mutationOptions: UseMutationOptions<RemoveCaseFromGroupResponse, AxiosError<RemoveCaseFromGroupError>, Options<RemoveCaseFromGroupData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await removeCaseFromGroup({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Add Case To Group
 */
export const addCaseToGroupMutation = (options?: Partial<Options<AddCaseToGroupData>>): UseMutationOptions<AddCaseToGroupResponse, AxiosError<AddCaseToGroupError>, Options<AddCaseToGroupData>> => {
    const mutationOptions: UseMutationOptions<AddCaseToGroupResponse, AxiosError<AddCaseToGroupError>, Options<AddCaseToGroupData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await addCaseToGroup({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Create Release
 */
export const createReleaseMutation = (options?: Partial<Options<CreateReleaseData>>): UseMutationOptions<CreateReleaseResponse, AxiosError<CreateReleaseError>, Options<CreateReleaseData>> => {
    const mutationOptions: UseMutationOptions<CreateReleaseResponse, AxiosError<CreateReleaseError>, Options<CreateReleaseData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await createRelease({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const getReleaseQueryKey = (options: Options<GetReleaseData>) => createQueryKey('getRelease', options, false, ['golden', 'test-groups']);

/**
 * Get Release
 */
export const getReleaseOptions = (options: Options<GetReleaseData>) => queryOptions<GetReleaseResponse, AxiosError<GetReleaseError>, GetReleaseResponse, ReturnType<typeof getReleaseQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await getRelease({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: getReleaseQueryKey(options)
});

/**
 * Import Documents
 *
 * Start the document import job for a case. already_running is true
 * when an import of the case was running. 409 when this deployment imports no
 * documents or the case has no baseline yet; 503 when the job cannot start.
 */
export const importDocumentsMutation = (options?: Partial<Options<ImportDocumentsData>>): UseMutationOptions<ImportDocumentsResponse, AxiosError<ImportDocumentsError>, Options<ImportDocumentsData>> => {
    const mutationOptions: UseMutationOptions<ImportDocumentsResponse, AxiosError<ImportDocumentsError>, Options<ImportDocumentsData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await importDocuments({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Create Suite
 *
 * Start a suite: the deployed RUMI on a test group (frozen as a release)
 * or on an earlier release, compared in its report with each case's latest
 * earlier runs on another recorded version.
 *
 * The trigger sets the run limit, so it follows the caller: platform CI
 * (rumi-ci) starts only ci suites; manual and nightly suites need
 * rumi-tester.
 *
 * 400 for an unknown trigger or replay kind, repetitions outside 1 to 10, both
 * or neither of test_group and release, or more runs than the trigger's limit;
 * 404 for a test group or release that does not exist; 503 when the suite may
 * not have started, naming the start route that starts it.
 */
export const createSuiteMutation = (options?: Partial<Options<CreateSuiteData>>): UseMutationOptions<CreateSuiteResponse, AxiosError<CreateSuiteError>, Options<CreateSuiteData>> => {
    const mutationOptions: UseMutationOptions<CreateSuiteResponse, AxiosError<CreateSuiteError>, Options<CreateSuiteData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await createSuite({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const getSuiteQueryKey = (options: Options<GetSuiteData>) => createQueryKey('getSuite', options, false, ['golden', 'suites']);

/**
 * Get Suite
 */
export const getSuiteOptions = (options: Options<GetSuiteData>) => queryOptions<GetSuiteResponse, AxiosError<GetSuiteError>, GetSuiteResponse, ReturnType<typeof getSuiteQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await getSuite({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: getSuiteQueryKey(options)
});

/**
 * Start Suite
 *
 * Start a queued suite whose start failed, or may have. Starting it
 * again is safe: its workflow id is fixed. The trigger rule is createSuite's.
 *
 * 403 when the caller may not start the suite's trigger; 404 for a suite that
 * does not exist; 409 for a suite that is not queued; 503 when the suite may
 * not have started.
 */
export const startSuiteMutation = (options?: Partial<Options<StartSuiteData>>): UseMutationOptions<StartSuiteResponse, AxiosError<StartSuiteError>, Options<StartSuiteData>> => {
    const mutationOptions: UseMutationOptions<StartSuiteResponse, AxiosError<StartSuiteError>, Options<StartSuiteData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await startSuite({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const getSuiteReportQueryKey = (options: Options<GetSuiteReportData>) => createQueryKey('getSuiteReport', options, false, ['golden', 'suites']);

/**
 * Get Suite Report
 *
 * The comparison report. With ``limit``, it also says whether the suite
 * passes the CI limit; only a finished suite passes. A run not yet scored at its
 * release's revision is scored then, with no judge; the finishing suite scores
 * that free text again with its judge. 400 for a limit that is not a whole number
 * from 0.
 */
export const getSuiteReportOptions = (options: Options<GetSuiteReportData>) => queryOptions<GetSuiteReportResponse, AxiosError<GetSuiteReportError>, GetSuiteReportResponse, ReturnType<typeof getSuiteReportQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await getSuiteReport({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: getSuiteReportQueryKey(options)
});
